import { useContext, useEffect, useMemo, useState } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import { cn } from '@/lib/utils';
import { ChevronDown, ChevronUp } from 'lucide-react';
import { NavigationMenu, NavigationMenuItem, NavigationMenuLink, NavigationMenuList } from './ui/navigation-menu';
import { useUserStore } from '@/stores/user';
import { NavigationBlockerContext } from '@/context/NavigationBlockerContext';
import { UserResponseSchema } from '@brainvaulttech/dgr-react-sdk';
import AlertBox from './AlertBox';
import { hasFeature } from '@/services/featureService';
import { isBillingTeamSupervisor, isSupervisor } from '@/services/userService';
import { useFeatureStore } from '@/stores/featureStore';
import { Feature } from '@/utils/constant';

export interface SidebarItem {
  name: string;
  path: string;
  icon?: React.ComponentType;
  roles?: string[];
  teamKeys?: string[];
  allowSupervisor?: boolean;
  allowBillingSupervisor?: boolean;
  requiredFeature?: Feature;
  requiredFeatures?: Feature[];
  featureOperator?: 'AND' | 'OR';
  children?: SidebarItem[];
  additionalPaths?: string[];
}

export const ALL_SIDEBAR_ITEMS: SidebarItem[] = [
  {
    name: 'Dashboard',
    path: '/dashboard',
    requiredFeature: Feature.DASHBOARD,
  },
  {
    name: 'Users',
    path: '/users',
    roles: ['ADMIN'],
    requiredFeature: Feature.USERS,
  },
  {
    name: 'Clients',
    path: '/clients',
    requiredFeature: Feature.CLIENTS,
  },

  {
    name: 'Courts',
    path: '/courts',
    roles: ['ADMIN', 'OPERATOR'],
    requiredFeature: Feature.COURTS,
  },
  {
    name: 'Services',
    path: '/process',
    requiredFeatures: [Feature.SERVICES, Feature.DELIVERY],
    featureOperator: 'OR',
    additionalPaths: ['/delivery'],
  },
  {
    name: 'Due Diligence',
    path: '/due-diligence',
    requiredFeature: Feature.DUE_DILIGENCE_WORK_ORDER_READ,
  },
  {
    name: 'Vendors',
    path: '/vendors',
    roles: ['ADMIN', 'OPERATOR'],
    requiredFeature: Feature.VENDORS,
  },
  {
    name: 'Recaps',
    path: '/recaps',
    roles: ['ADMIN', 'ACCOUNTANT', 'BILLING'],
    teamKeys: ['BILLING', 'ACCOUNTING'],
    allowSupervisor: true,
    requiredFeature: Feature.RECAPS,
  },
  {
    name: 'Final Billing Summary',
    path: '/final-billing-summary',
    roles: ['ADMIN', 'ACCOUNTANT', 'BILLING'],
    teamKeys: ['BILLING', 'ACCOUNTING'],
    allowBillingSupervisor: true,
    requiredFeature: Feature.FBS,
  },
  {
    name: 'Accounting',
    path: '/accounts',
    roles: ['ACCOUNTANT', 'ADMIN'],
    teamKeys: ['ACCOUNTING'],
    allowSupervisor: true,
    requiredFeature: Feature.ACCOUNTS,
    children: [
      {
        name: 'Accounts Receivable',
        path: 'receivable',
        roles: ['ADMIN', 'ACCOUNTANT'],
        teamKeys: ['ACCOUNTING'],
        requiredFeature: Feature.ACCOUNTS_RECEIVABLE,
      },
      {
        name: 'Vendor Payment',
        path: 'vendor-payment',
        roles: ['ADMIN', 'ACCOUNTANT'],
        teamKeys: ['ACCOUNTING'],
        requiredFeature: Feature.VENDOR_PAYMENT,
      },
      {
        name: 'Check Register',
        path: 'check-register',
        roles: ['ADMIN', 'ACCOUNTANT'],
        teamKeys: ['ACCOUNTING'],
        requiredFeature: Feature.CHECK_REGISTER,
      },
      {
        name: 'Payment History',
        path: 'payment-history',
        roles: ['ADMIN', 'ACCOUNTANT'],
        teamKeys: ['ACCOUNTING'],
        requiredFeature: Feature.PAYMENT_HISTORY,
      },
      {
        name: 'Statements',
        path: 'statements',
        roles: ['ADMIN', 'ACCOUNTANT'],
        teamKeys: ['ACCOUNTING'],
        allowSupervisor: true,
        requiredFeature: Feature.STATEMENTS,
      },
    ],
  },
];

export const filterSidebarItems = (
  items: SidebarItem[],
  userRole: string,
  userTeamKeys: string[],
  isSup = false,
  isBillingSup = false
): SidebarItem[] => {
  return items
    .map((item) => {
      const hasRoleAccess =
        !item.roles ||
        item.roles.includes(userRole) ||
        (!!item.teamKeys?.length && item.teamKeys.some((key) => userTeamKeys.includes(key))) ||
        (!!item.allowSupervisor && isSup) ||
        (!!item.allowBillingSupervisor && isBillingSup);
      let hasFeatureAccess = true;

      // Check single required feature
      if (item.requiredFeature && !hasFeature(item.requiredFeature)) {
        hasFeatureAccess = false;
      }

      // Check multiple required features with operator
      if (item.requiredFeatures && item.requiredFeatures.length > 0) {
        if (item.featureOperator === 'OR') {
          // OR logic: at least one feature must be enabled
          hasFeatureAccess = hasFeatureAccess && item.requiredFeatures.some((feature) => hasFeature(feature));
        } else {
          // AND logic (default): all features must be enabled
          hasFeatureAccess = hasFeatureAccess && item.requiredFeatures.every((feature) => hasFeature(feature));
        }
      }

      // If no access, skip
      if (!hasRoleAccess || !hasFeatureAccess) return null;

      // If item has children, filter them recursively
      let filteredChildren: SidebarItem[] | undefined = undefined;
      if (item.children) {
        filteredChildren = filterSidebarItems(item.children, userRole, userTeamKeys, isSup, isBillingSup);
      }

      return {
        ...item,
        children: filteredChildren,
      };
    })
    .filter(Boolean) as SidebarItem[];
};

const Sidebar = () => {
  const navigate = useNavigate();
  const location = useLocation();
  const [selected, setSelected] = useState<string | null>(null);
  const [expandedItems, setExpandedItems] = useState<Record<string, boolean>>({});
  const user = useUserStore((state) => state.user);
  const userRole = (user as UserResponseSchema)?.roleId;
  const userTeamKeys = user?.teams?.map((team) => team.key) ?? [];
  const isSup = isSupervisor(user);
  const isBillingSup = isBillingTeamSupervisor(user);
  const features = useFeatureStore((state) => state.features);
  const [showAlertPath, setShowAlertPath] = useState<string | undefined>(undefined);
  const { isDirty, setIsDirty } = useContext(NavigationBlockerContext);

  const sidebarItems = useMemo(() => {
    return filterSidebarItems(ALL_SIDEBAR_ITEMS, userRole ?? '', userTeamKeys, isSup, isBillingSup);
  }, [userRole, userTeamKeys, features, isSup, isBillingSup]);

  useEffect(() => {
    const currentPath = location.pathname;
    setSelected(currentPath);

    const expandParents = (items: SidebarItem[], parentPath = '') => {
      items.forEach((item) => {
        const fullPath = parentPath ? `${parentPath}/${item.path}` : item.path;
        if (item.children && currentPath.startsWith(fullPath)) {
          setExpandedItems((prev) => ({
            ...prev,
            [fullPath]: true,
          }));
          expandParents(item.children, fullPath);
        }
      });
    };

    expandParents(sidebarItems);
  }, [location, sidebarItems]);

  const toggleExpand = (path: string) => {
    setExpandedItems((prev) => ({
      ...prev,
      [path]: !prev[path],
    }));
  };
  const handleNavigate = (path: string) => {
    if (isDirty) {
      setShowAlertPath(path);
    } else {
      navigate(path);
    }
  };

  const renderSidebarItems = (items: SidebarItem[], parentPath = '') => {
    return items
      .filter(
        (i) =>
          !i.roles ||
          i.roles.includes(userRole) ||
          (!!i.teamKeys?.length && i.teamKeys.some((key) => userTeamKeys.includes(key))) ||
          (!!i.allowSupervisor && isSup) ||
          (!!i.allowBillingSupervisor && isBillingSup)
      )
      .map((item) => {
        const fullPath = parentPath ? `${parentPath}/${item.path}` : item.path;
        const isExpanded = expandedItems[fullPath] || false;
        const isSelected =
          (selected?.startsWith(`${fullPath}/`) ? selected?.startsWith(`${fullPath}/`) : selected === fullPath) ||
          item.additionalPaths?.some((path) => selected?.startsWith(path));
        const shouldHighlightParent = !isExpanded && isSelected;
        return (
          <NavigationMenuItem key={fullPath} className="!ml-0">
            <div className={`flex items-center gap-0`} onClick={() => handleNavigate(item.children ? '#' : fullPath)}>
              <NavigationMenuLink
                asChild
                className={cn(
                  'p-3 mt-1 rounded-md flex  items-center gap-3 min-w-[50vw] md:min-w-[100%] ',
                  shouldHighlightParent ? 'bg-[#65768C] text-white' : '',
                  !item.children ? 'hover:bg-[#65768C] hover:text-white justify-start' : ' justify-between'
                )}
                onClick={(e) => {
                  if (item.children) {
                    e.preventDefault();
                    toggleExpand(fullPath);
                  }
                }}
              >
                <div className="flex justify-between">
                  <span className="text-sm">{item.name}</span>
                  {item.children && (
                    <button>
                      {isExpanded ? <ChevronUp className="h-4 w-4" /> : <ChevronDown className="h-4 w-4" />}
                    </button>
                  )}
                </div>
              </NavigationMenuLink>
            </div>

            {item.children && isExpanded && (
              <NavigationMenuList className="pl-4 flex flex-col w-full gap-0">
                {renderSidebarItems(item.children, fullPath)}
              </NavigationMenuList>
            )}
          </NavigationMenuItem>
        );
      });
  };

  useEffect(() => {
    setIsDirty(isDirty);
  }, [isDirty]);

  return (
    <>
      <div
        className={cn(
          'w-full md:w-[300px] h-[100vh] bg-primary md:bg-primary flex justify-between flex-col md:py-[16px] text-[16px] md:pt-[75px] fixed z-1 md:min-w-[180px] md:max-w-[250px] overflow-y-scroll scrollbar-hide '
        )}
      >
        <NavigationMenu>
          <NavigationMenuList className="flex cursor-pointer flex-col w-full text-[#fff] gap-2 px-3">
            {renderSidebarItems(sidebarItems)}
          </NavigationMenuList>
        </NavigationMenu>
      </div>
      {showAlertPath && (
        <AlertBox
          open={true}
          description="Changes which you made might not be saved."
          title="Are you sure?"
          confirmBtnClick={() => {
            setShowAlertPath(undefined);
            navigate(showAlertPath);
            setIsDirty(false);
          }}
          warning
          cancelBtnClick={() => setShowAlertPath(undefined)}
          confirmBtnLabel="Yes"
          cancelBtnLabel="No"
        />
      )}
    </>
  );
};
export default Sidebar;
